import { useEffect, useState } from 'react';

/**
 * How many panes the bookings page has room for.
 *
 * ONE SOURCE for the tiers, read by both the hook below and the styled-components media queries
 * that lay the panes out. The app already had two tiers that disagreed by a pixel -- the JS said
 * small at `width <= 768` while the CSS said small at `max-width: 769px`, so a viewport sitting
 * exactly on 769 got the desktop answer from one and the mobile answer from the other. Deriving
 * both from these numbers is what stops a third tier inheriting that.
 *
 * The widths are the CONTENT's, not a device's, and they are measured inside the FRAME:
 * `window.innerWidth` in an iframe is the iframe's width, which on the portal is already the
 * viewport minus its 256px sidebar. So these are deliberately not the portal's own numbers, and a
 * 1512px laptop is a ~1256px frame here.
 */
export const SINGLE_PANE_MAX_WIDTH = 768;

/*
 * The columns' own measurements, declared ONCE here and laid out from here too (see
 * pages/bookings/styles.ts, which imports them).
 *
 * The number that decides whether three columns fit and the three columns that are actually drawn
 * used to be separate facts -- a flat threshold here, widths over there -- so changing either one
 * silently made the other wrong. Derived, they cannot disagree.
 */
export const LIST_PANE_WIDTH = 336;
/*
 * ⚠️ THE FLOOR, AND THE NUMBER EVERY THRESHOLD BELOW IS BUILT ON. The list is a PREFERRED 336 that
 * gives way before the booking does -- tile titles truncate and nothing reflows, so 16px off this
 * column buys the booking 16px it can actually use. What must not move is the point at which three
 * columns stop fitting: that is a question about the narrowest the list is allowed to be, not the
 * widest it would like to be, so the arithmetic below reads this one and never LIST_PANE_WIDTH.
 */
export const LIST_PANE_MIN_WIDTH = 320;
export const MESSAGES_PANE_WIDTH = 360;
export const PANE_GAP = 24;
export const PANE_GUTTER = 42;

/*
 * How narrow the booking column may get while the conversation still has a column of its own.
 *
 * RES-1367: this was stated the other way round -- a flat 1440px frame, which happened to leave the
 * booking 628px -- and the effect was that the conversation dropped under the booking while the
 * booking still had two hundred pixels it could have given up first. The booking is a card of short
 * labelled rows, not a document: checked at 400 it still reads fine, and it is the two action
 * buttons side by side that finally run out of room below that. So the MINIMUM is the thing worth
 * stating, and the threshold is whatever that minimum plus the fixed columns around it adds up to.
 */
export const MIN_DETAIL_PANE_WIDTH = 400;

/*
 * The list is at its floor, the conversation is fixed, the two gaps and the page's own gutters take their cut,
 * and the booking gets the slack -- so three columns fit exactly when that slack is still at least
 * MIN_DETAIL_PANE_WIDTH. Works out to 1212 in frame coordinates, roughly a 1468px viewport once the
 * portal's 256px sidebar is accounted for: a 1512px laptop now keeps all three columns, where
 * before it dropped to two and left the booking sprawling across the width of both.
 */
export const THREE_PANE_MIN_WIDTH =
  MIN_DETAIL_PANE_WIDTH + LIST_PANE_MIN_WIDTH + MESSAGES_PANE_WIDTH + 2 * PANE_GAP + 2 * PANE_GUTTER;

/** The same tiers as media queries, so a stylesheet cannot drift from the hook. */
export const PANE_MEDIA = {
  singlePane: `(max-width: ${SINGLE_PANE_MAX_WIDTH}px)`,
  twoPaneOrWider: `(min-width: ${SINGLE_PANE_MAX_WIDTH + 1}px)`,
  threePane: `(min-width: ${THREE_PANE_MIN_WIDTH}px)`,
} as const;

export type PaneLayout = 'single' | 'two' | 'three';

export const paneLayoutForWidth = (width: number): PaneLayout => {
  if (width <= SINGLE_PANE_MAX_WIDTH) return 'single';

  return width >= THREE_PANE_MIN_WIDTH ? 'three' : 'two';
};

/**
 * Which pane layout the frame currently has room for.
 *
 * Read synchronously on the FIRST render rather than settling on a second pass. A hook that starts
 * out undefined and corrects itself answers "widest" to every reader for one paint, and a layout
 * built on that flashes three panes onto a phone before collapsing them. The initialiser reads
 * `window` directly, so the first answer is already the right one.
 *
 * Subscribes to `resize` ITSELF rather than borrowing `useWindowDimensions`, which lives in
 * `use-is-small-viewport-width.hook`. That module is auto-mocked by 57 specs -- for the small-
 * viewport hook beside it -- and an automock turns every export into a function returning
 * undefined, so sharing it would make this hook throw in any spec that happened to mock its
 * neighbour. The few lines of subscription are cheaper than that coupling, and a three-tier layout
 * decision has no business reaching into a hook named for one boolean.
 */
export const usePaneLayout = (): PaneLayout => {
  const [layout, setLayout] = useState<PaneLayout>(() => paneLayoutForWidth(window.innerWidth));

  useEffect(() => {
    const syncLayout = () => setLayout(paneLayoutForWidth(window.innerWidth));

    syncLayout();
    window.addEventListener('resize', syncLayout);

    return () => window.removeEventListener('resize', syncLayout);
  }, []);

  return layout;
};
